/**
 * @file ConfigSchema.ts
 * @role Zod schemas for bundled config. Config is validated at boot just like backend
 *       data — a malformed config is a hostile input that must fail loud, not silently
 *       feed garbage (wrong currency, negative bet) into the game.
 * @owns The GameConfig schema + the MockScenario enum (shared by env + backend mock).
 * @reads Nothing.
 * @neverDoes Load files. ConfigService does that. Decide game outcomes.
 * @invariants 7 (money is integer minor units — enforced here as int constraints), 10.
 * @see docs/CONFIG_GUIDE.md
 */
import { z } from 'zod';

/** The deterministic fake-server scenarios served by src/mocks/rgsScenarios.ts. */
export const MockScenarioSchema = z.enum([
  'no_win',
  'small_win',
  'medium_win',
  'line_win',
  'scatter_win',
  'bonus_trigger',
  'big_win',
  'jackpot_win',
  'insufficient_balance',
  'session_expired',
  'network_timeout',
  'malformed_response',
  'slow_response',
  'demo_cycle',
]);
export type MockScenario = z.infer<typeof MockScenarioSchema>;

export const GameConfigSchema = z
  .object({
    gameId: z.string().min(1),
    /** ISO-4217 alpha code, e.g. "EUR". */
    currency: z.string().length(3),
    /** Default bet in integer minor units (Invariant 7). */
    defaultBetMinor: z.number().int().safe().positive(),
    betStepsMinor: z.array(z.number().int().safe().positive()).min(1),
    /** Hung-backend watchdog for spin requests (Invariant 21: reels never spin forever). */
    spinTimeoutMs: z.number().int().positive(),
    mockRgs: z.object({
      enabled: z.boolean(),
      defaultScenario: MockScenarioSchema,
      responseDelayMs: z.number().int().nonnegative(),
      startingBalanceMinor: z.number().int().safe().nonnegative(),
    }),
    /** The real RGS. Used whenever mockRgs.enabled is false; baseUrl comes from
     *  VITE_RGS_BASE_URL at build time. See docs/RGS_SERVER_REQUIREMENTS.md. */
    rgs: z.object({
      baseUrl: z.string().min(1),
      /** The loaded RGS catalog id (e.g. "5x3-l2r-96"). */
      gameId: z.string().min(1),
      brand: z.string().min(1),
      /** Placeholder identity until the operator mints a real signed launch token. */
      userId: z.string().min(1),
      /** Symbol that marks a feature trigger on the base screen. */
      scatterSymbolId: z.string().min(1),
    }),
  })
  .superRefine((config, ctx) => {
    if (!config.betStepsMinor.includes(config.defaultBetMinor)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['betStepsMinor'],
        message: 'betStepsMinor must include defaultBetMinor',
      });
    }
  });
export type GameConfig = z.infer<typeof GameConfigSchema>;
